import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "@/components/bits";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Plus, Send, Bug, Lightbulb, HelpCircle, MessageSquare } from "lucide-react";

const CATEGORIES = [
  { id: "BUG", label: "Fehler melden" },
  { id: "FEATURE", label: "Funktionswunsch" },
  { id: "FRAGE", label: "Frage" },
];

const STATUS_META = {
  OPEN: { label: "Offen", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  IN_PROGRESS: { label: "In Arbeit", cls: "text-blue-400 border-blue-400/40 bg-blue-400/10" },
  RESOLVED: { label: "Gelöst", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  CLOSED: { label: "Geschlossen", cls: "text-slate-400 border-slate-400/40 bg-slate-400/10" },
};

const FILTERS = [
  { id: "", label: "Alle" },
  { id: "OPEN", label: "Offen" },
  { id: "IN_PROGRESS", label: "In Arbeit" },
  { id: "RESOLVED", label: "Gelöst" },
  { id: "CLOSED", label: "Geschlossen" },
];

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.OPEN;
  return (
    <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function CategoryIcon({ category }) {
  const Icon = category === "FEATURE" ? Lightbulb : category === "FRAGE" ? HelpCircle : Bug;
  return <Icon size={15} className="text-cyan-400 shrink-0" />;
}

export default function Support() {
  const { user } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState("");
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("BUG");
  const [message, setMessage] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/tickets${filter ? `?status=${filter}` : ""}`).then((r) => setTickets(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [filter]);

  useEffect(() => {
    if (isSuper) {
      api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  const visibleTickets = tickets && tenantFilter ? tickets.filter((t) => t.tenant_id === tenantFilter) : tickets;

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!selected) return undefined;
    const fetchDetail = () =>
      api.get(`/tickets/${selected.id}`).then((r) => setSelected(r.data)).catch(() => {});
    fetchDetail();
    const t = setInterval(fetchDetail, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  async function createTicket() {
    if (!subject.trim() || !message.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/tickets", { subject: subject.trim(), category, message: message.trim() });
      toast.success("Ticket angelegt — der Support wurde informiert");
      setCreateOpen(false);
      setSubject("");
      setMessage("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function sendComment() {
    if (!comment.trim() || !selected || busy) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${selected.id}/comments`, { message: comment.trim() });
      setComment("");
      const { data } = await api.get(`/tickets/${selected.id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(status) {
    try {
      await api.patch(`/tickets/${selected.id}`, { status });
      toast.success(`Status: ${STATUS_META[status].label}`);
      const { data } = await api.get(`/tickets/${selected.id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  const myUnread = (t) => (isSuper ? t.unread_admin : t.unread_tenant);

  return (
    <div data-testid="support-page">
      <PageHeader
        title="Support"
        sub={isSuper ? "Tickets aller Mandanten — Fehler, Wünsche, Fragen" : "Direkter Draht zum Support — Fehler melden, Wünsche äußern"}
      >
        {!isSuper && (
          <button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="ticket-create-open">
            <Plus size={15} /> Neues Ticket
          </button>
        )}
      </PageHeader>

      {isSuper && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="ticket-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-sm border font-mono text-[11px] uppercase tracking-widest transition-colors duration-150 ${
                filter === f.id ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-border text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`ticket-filter-${f.id || "alle"}`}
            >
              {f.label}
            </button>
          ))}
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="input-dark !w-auto !py-1.5 text-xs ml-auto"
            data-testid="ticket-tenant-filter"
          >
            <option value="">Alle Mandanten</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Ticket-Liste */}
        <div className="lg:col-span-2 space-y-2" data-testid="ticket-list">
          {visibleTickets === null ? (
            <div className="font-mono text-xs text-cyan-400 animate-pulse">Lade Tickets…</div>
          ) : visibleTickets.length === 0 ? (
            <EmptyState text={isSuper ? "Keine Tickets — alles ruhig" : "Noch keine Tickets — melde dich bei Fragen"} testid="tickets-empty" />
          ) : (
            visibleTickets.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setSelected(t)}
                className={`w-full text-left panel p-4 transition-colors duration-150 hover:border-cyan-500/40 ${
                  selected?.id === t.id ? "!border-cyan-500/60 bg-cyan-500/5" : ""
                }`}
                data-testid={`ticket-row-${i}`}
              >
                <div className="flex items-start gap-2.5">
                  <CategoryIcon category={t.category} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-100 truncate font-medium">{t.subject}</span>
                      {myUnread(t) && (
                        <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" data-testid={`ticket-unread-${i}`} title="Ungelesen" />
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-1.5">
                      <StatusChip status={t.status} />
                      {isSuper && <span className="font-mono text-[10px] text-slate-400">{t.tenant_name}</span>}
                      <span className="font-mono text-[10px] text-slate-400 flex items-center gap-1">
                        <MessageSquare size={9} /> {t.comment_count}
                      </span>
                      <span className="font-mono text-[10px] text-slate-400 ml-auto">{fmtTime(t.updated_at)}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail / Thread */}
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="panel p-8 text-center" data-testid="ticket-detail-empty">
              <p className="font-mono text-xs text-slate-400">Ticket auswählen, um Verlauf und Antworten zu sehen</p>
            </div>
          ) : (
            <div className="panel p-5" data-testid="ticket-detail">
              <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-border/60">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CategoryIcon category={selected.category} />
                    <span className="font-display font-bold text-slate-100" data-testid="ticket-detail-subject">{selected.subject}</span>
                  </div>
                  <div className="font-mono text-[10px] text-slate-400 mt-1.5">
                    {selected.author_name || selected.author_email}
                    {isSuper && selected.tenant_name ? ` · ${selected.tenant_name}` : ""} · {fmtTime(selected.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isSuper ? (
                    <select
                      value={selected.status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="input-dark !w-auto !py-1.5 text-xs"
                      data-testid="ticket-status-select"
                    >
                      {Object.entries(STATUS_META).map(([id, m]) => (
                        <option key={id} value={id}>{m.label}</option>
                      ))}
                    </select>
                  ) : (
                    selected.status !== "CLOSED" && (
                      <button onClick={() => setStatus("CLOSED")} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="ticket-close-button">
                        Ticket schließen
                      </button>
                    )
                  )}
                </div>
              </div>

              <div className="py-4 space-y-3 max-h-80 overflow-y-auto" data-testid="ticket-thread">
                <div className="rounded-sm border border-border bg-slate-900/40 p-3">
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{selected.message}</div>
                </div>
                {(selected.comments || []).map((c) => {
                  const own = c.author_email === user?.email;
                  return (
                    <div key={c.id} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`ticket-comment-${c.id.slice(0, 8)}`}>
                      <div
                        className={`max-w-[85%] rounded-sm border p-3 ${
                          c.author_role === "SUPER_ADMIN"
                            ? "border-cyan-500/30 bg-cyan-500/5"
                            : "border-border bg-slate-900/40"
                        }`}
                      >
                        <div className="font-mono text-[10px] text-slate-400 mb-1">
                          {c.author_role === "SUPER_ADMIN" ? "Support" : c.author_name || c.author_email} · {fmtTime(c.created_at)}
                        </div>
                        <div className="text-sm text-slate-200 whitespace-pre-wrap">{c.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selected.status !== "CLOSED" ? (
                <div className="flex gap-2 pt-4 border-t border-border/60">
                  <input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendComment()}
                    placeholder="Antwort schreiben…"
                    className="input-dark"
                    data-testid="ticket-comment-input"
                  />
                  <button onClick={sendComment} disabled={!comment.trim() || busy} className="btn-primary !px-3.5" data-testid="ticket-comment-send">
                    <Send size={15} />
                  </button>
                </div>
              ) : (
                <p className="pt-4 border-t border-border/60 font-mono text-[11px] text-slate-400" data-testid="ticket-closed-hint">
                  Ticket geschlossen — bei Bedarf einfach ein neues anlegen.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Neues Ticket */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="ticket-create-dialog">
          <DialogTitle>Neues Ticket</DialogTitle>
          <div className="space-y-4 mt-2">
            <div>
              <div className="mono-label mb-1.5">Art</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-dark" data-testid="ticket-category-select">
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="mono-label mb-1.5">Betreff</div>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Kurz und knackig, z. B. ‚Display bleibt schwarz‘"
                className="input-dark"
                data-testid="ticket-subject-input"
              />
            </div>
            <div>
              <div className="mono-label mb-1.5">Beschreibung</div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Was ist passiert? Welches Gerät / welche Ansicht? Gern mit Uhrzeit."
                className="input-dark resize-none"
                data-testid="ticket-message-input"
              />
            </div>
            <button onClick={createTicket} disabled={!subject.trim() || !message.trim() || busy} className="btn-primary w-full" data-testid="ticket-create-submit">
              {busy ? "Sende…" : "Ticket absenden"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
